/**
 * S2 (C3 reconciled build plan — docs/architecture/REALTIME-STREAMING.md):
 * snapshot barrier + Redis-recovery resync.
 *
 * Fire-and-forget `invalidateQueries` in onopen is NOT sufficient: a stale
 * REST response can resolve AFTER a newer live delta was applied and
 * overwrite it. The barrier makes the ordering explicit:
 *
 *   1. On socket (re)connect the leader marks trading ingestion
 *      `reconciling` (`beginSnapshotBarrier`) and BUFFERS inbound trading
 *      frames (positions/orders/fills).
 *   2. When the backend `subscription.confirmed` arrives (its channel
 *      subscriptions are live), `runSnapshotReconciliation` AWAITS
 *      `refetchQueries` on the scoped truth keys with `cancelRefetch: true`
 *      (stale in-flight fetches are cancelled, fresh REST truth lands).
 *   3. Buffered frames replay IN ARRIVAL ORDER on top of that snapshot —
 *      a live delta that raced the reconciliation therefore WINS over the
 *      REST snapshot. Then ingestion returns to normal live apply.
 *
 * The backend `system.resync_required` event (emitted after a RedisSubscriber
 * recovery, while the browser socket stayed open) runs the SAME
 * reconciliation from idle: buffer → refetch → replay. This is what makes the
 * Redis-Streams deferral honest — eventual dashboard state via REST truth.
 *
 * `*.initial` trading snapshots are NOT buffered — they apply immediately
 * (hydrate-from-snapshot-first). The REST refetch on confirm still runs and
 * a live delta that raced reconciliation still wins because those frames
 * stay buffered and replay after REST. Empty/error snapshots must never
 * wipe a painted book (the applier no-ops on []).
 */

type AnyRecord = Record<string, any>;

/** Channels whose frames are buffered while the barrier is reconciling. */
export const BUFFERED_TRADING_CHANNELS: ReadonlySet<string> = new Set([
  "positions",
  "orders",
  "fills",
  // CV-005: chart snapshot revisions — a revision frame racing the reconnect
  // refetch must replay AFTER the REST truth lands, not be dropped or applied
  // against a stale snapshot.
  "chart_features",
]);

/**
 * The scoped truth keys the reconciliation refetches (contract S2:
 * positions/orders/fills/accounts/portfolio-metrics — plus the profile/context
 * mirrors those surfaces render from).
 */
export const TRUTH_REFETCH_KEYS: string[][] = [
  ["positions"],
  ["profile-positions"],
  ["orders"],
  ["fills"],
  ["accounts"],
  ["profile-accounts"],
  ["account-context"],
  ["portfolio-metrics"],
  // CV-005: current chart snapshot revisions (REST-authoritative bytes).
  ["chart-features"],
];

/**
 * Safety valve: if `subscription.confirmed` never arrives (backend deploy
 * skew, dropped frame), complete the reconciliation anyway so buffered
 * trading frames don't hold the dashboard dark forever.
 */
export const BARRIER_CONFIRM_TIMEOUT_MS = 10_000;

interface BarrierState {
  reconciling: boolean;
  buffer: AnyRecord[];
  safetyTimer: ReturnType<typeof setTimeout> | null;
  /** Bumped on begin/reset — an awaited reconciliation from a previous epoch
   *  must abandon its replay instead of clobbering the new connection's. */
  epoch: number;
}

const barriers = new Map<number, BarrierState>();

const stateFor = (connectionId: number): BarrierState => {
  let state = barriers.get(connectionId);
  if (!state) {
    state = { reconciling: false, buffer: [], safetyTimer: null, epoch: 0 };
    barriers.set(connectionId, state);
  }
  return state;
};

const clearSafetyTimer = (state: BarrierState): void => {
  if (state.safetyTimer) {
    clearTimeout(state.safetyTimer);
    state.safetyTimer = null;
  }
};

/**
 * Mark trading ingestion `reconciling` for this connection (leader socket
 * onopen, BEFORE the subscribe frame is sent). `onSafetyTimeout` fires if no
 * `subscription.confirmed` completes the barrier within the timeout — the
 * caller wires it to the same reconciliation it would run on confirm.
 */
export const beginSnapshotBarrier = (
  connectionId: number,
  onSafetyTimeout: () => void,
): void => {
  const state = stateFor(connectionId);
  clearSafetyTimer(state);
  state.reconciling = true;
  state.buffer = [];
  state.epoch += 1;
  state.safetyTimer = setTimeout(() => {
    state.safetyTimer = null;
    if (state.reconciling) {
      console.warn(
        `[snapshot-barrier] no subscription.confirmed within ${BARRIER_CONFIRM_TIMEOUT_MS}ms — completing reconciliation via safety timeout`,
      );
      onSafetyTimeout();
    }
  }, BARRIER_CONFIRM_TIMEOUT_MS);
};

export const isReconciling = (connectionId: number): boolean =>
  barriers.get(connectionId)?.reconciling === true;

const isInitialTradingSnapshot = (frame: AnyRecord): boolean =>
  String(frame?.type ?? frame?.topic ?? "").endsWith(".initial");

/**
 * Buffer `frame` if this connection is reconciling AND the frame belongs to a
 * buffered trading channel. Returns true when buffered (caller must NOT route
 * the frame further).
 */
export const bufferBarrierFrame = (
  connectionId: number,
  routedChannel: string,
  frame: AnyRecord,
): boolean => {
  const state = barriers.get(connectionId);
  if (!state?.reconciling || !BUFFERED_TRADING_CHANNELS.has(routedChannel)) {
    return false;
  }
  // Snapshot-first: let positions.initial (and sibling *.initial) flow
  // through to the applier during reconciling so the book paints from the
  // channel snapshot instead of waiting on REST.
  if (isInitialTradingSnapshot(frame)) {
    return false;
  }
  state.buffer.push(frame);
  return true;
};

/**
 * Run the reconciliation: (ensure buffering) → await refetch of the truth
 * keys → replay buffered frames in arrival order → resume live apply.
 *
 * Callable from two states:
 *  - reconciling (leader barrier begun at onopen; `subscription.confirmed`
 *    or the safety timeout completes it), or
 *  - idle (`system.resync_required`, or a follower notified of the leader's
 *    reconnect): buffering starts NOW so frames arriving mid-refetch are
 *    ordered after the snapshot.
 *
 * A refetch failure never wedges ingestion: buffered frames still replay and
 * the barrier returns to idle (the S5 health gate + trailing invalidations
 * own eventual recovery).
 */
export const runSnapshotReconciliation = async (
  connectionId: number,
  queryClient: AnyRecord,
  replay: (frame: AnyRecord) => void,
): Promise<void> => {
  const state = stateFor(connectionId);
  clearSafetyTimer(state);
  state.reconciling = true;
  const epoch = state.epoch;

  try {
    await Promise.all([
      ...TRUTH_REFETCH_KEYS.map((queryKey) =>
        queryClient.refetchQueries({ queryKey, cancelRefetch: true }),
      ),
      // SIG-431: the WS reconnect is the "backend is back" signal — also
      // refetch every query that ERRORED during the outage (brokers, user,
      // status surfaces…), not just the trading truth keys, so no surface
      // stays stuck on a cached failure until a hard refresh.
      queryClient.refetchQueries({
        predicate: (query: AnyRecord) => query.state?.status === "error",
        cancelRefetch: false,
      }),
    ]);
  } catch (error) {
    console.warn(
      "[snapshot-barrier] truth refetch failed — replaying buffered frames and resuming live apply",
      error,
    );
  }

  if (state.epoch !== epoch) {
    // A newer begin/reset owns the barrier (reconnect raced the refetch) —
    // abandon: the new epoch will reconcile with its own buffer.
    return;
  }

  // Flip to live BEFORE replay: replayed frames route through the normal
  // handler without being re-buffered. JS is single-threaded, so no new
  // frame can interleave into the synchronous replay loop.
  state.reconciling = false;
  const buffered = state.buffer;
  state.buffer = [];
  for (const frame of buffered) {
    if (isInitialTradingSnapshot(frame)) {
      continue; // the awaited refetch is strictly newer truth
    }
    replay(frame);
  }
};

/** Drop barrier state (socket closed / demoted / teardown). */
export const resetSnapshotBarrier = (connectionId: number): void => {
  const state = barriers.get(connectionId);
  if (!state) return;
  clearSafetyTimer(state);
  state.reconciling = false;
  state.buffer = [];
  state.epoch += 1;
};
